/**
 * DeleteConnectionDialog — type-to-confirm modal for deleting a Connections
 * registry row (connector secret or shared workspace).
 *
 * Replaces the bare ``window.confirm`` so destructive deletes match the house
 * dialog pattern (see ``DeleteNetworkDialog``): the user must TYPE the
 * connection name before the Delete button enables. The actual delete call is
 * owned by the caller (``onConfirm``) so this stays a presenter and the same
 * dialog serves both the connector and workspace delete surfaces.
 *
 * Decision logic (name match, submit gate) lives in ``utils/connectionDelete.js``.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  canSubmitConnectionDelete,
  confirmMatchesConnectionName,
} from '../../utils/connectionDelete.js';

export default function DeleteConnectionDialog({
  open,
  connectionName,
  isWorkspace = false,
  onConfirm,
  onClose,
}) {
  const [typedConfirm, setTypedConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Reset transient state every time the dialog opens for a (possibly
  // different) connection so a prior attempt can't leak into the next one.
  useEffect(() => {
    if (!open) return;
    setTypedConfirm('');
    setSubmitting(false);
    setSubmitError(null);
  }, [open, connectionName]);

  const confirmOk = confirmMatchesConnectionName(typedConfirm, connectionName);
  const canSubmit = canSubmitConnectionDelete({
    name: connectionName,
    typedConfirm,
    submitting,
  });

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onConfirm?.();
      // Caller closes on success (it also refreshes the list).
    } catch (err) {
      setSubmitError(err?.message || 'Delete failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            className="w-full max-w-lg bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
            initial={{ scale: 0.98, y: 6 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.98, y: 6 }}
          >
            <header className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  Delete {isWorkspace ? 'workspace' : 'connection'}
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                  <span className="font-medium text-gray-700">{connectionName}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-700 text-sm"
                aria-label="Close delete dialog"
                disabled={submitting}
              >
                ✕
              </button>
            </header>

            <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
                {isWorkspace ? (
                  <p>
                    This permanently removes the shared workspace registry row
                    and its stored files. This cannot be undone.
                  </p>
                ) : (
                  <p>
                    This removes the connection registry row. Stored credential
                    values may remain until rotated manually. This cannot be
                    undone.
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700 flex flex-col gap-1">
                  Type{' '}
                  <span className="font-mono text-[11px] bg-gray-100 rounded px-1 py-0.5">
                    {connectionName}
                  </span>{' '}
                  to confirm
                  <input
                    type="text"
                    value={typedConfirm}
                    onChange={(e) => {
                      setTypedConfirm(e.target.value);
                      setSubmitError(null);
                    }}
                    className={`mt-1 text-sm border rounded-lg px-3 py-2 ${
                      confirmOk
                        ? 'border-emerald-300 focus:ring-emerald-400 focus:border-emerald-400'
                        : 'border-gray-300 focus:ring-blue-400 focus:border-blue-400'
                    } outline-none focus:ring-2`}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={connectionName}
                    aria-label="Type connection name to confirm delete"
                    data-testid="delete-connection-confirm-input"
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                  />
                </label>
              </div>

              {submitError && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {submitError}
                </p>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-800 hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="text-sm px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Delete connection permanently"
                  data-testid="delete-connection-confirm-submit"
                >
                  {submitting
                    ? 'Deleting…'
                    : `Delete ${isWorkspace ? 'workspace' : 'connection'}`}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
